'use client';

import Image from 'next/image';
import Link from 'next/link';
import classes from './NeumoAbout.module.scss';
import {
  IconChecklist,
  IconDeviceImacCode,
  IconHeartHandshake,
  IconRoad,
  IconRocket,
  IconSparkles,
  IconTarget,
} from '@tabler/icons-react';

type ApproachItem = {
  icon?: 'target' | 'rocket' | 'handshake' | 'sparkles' | 'checklist' | 'road-map' | 'integration'; // string key from JSON
  strong: string;
  text: string;
};

type ValueCard = {
  icon?: string; // url
  title: string;
  description: string;
};

type NeumoAboutProps = {
  badge?: string;
  title: string;
  description: string;
  mission?: string;
  values?: ValueCard[];
  image?: string;
  imageAlt?: string;
  ctaLabel?: string;
  ctaHref?: string;

  // ✅ NEW: make the right card fully translatable
  approach?: {
    title: string;
    items: ApproachItem[];
  };
};

const iconMap = {
  target: IconTarget,
  rocket: IconRocket,
  handshake: IconHeartHandshake,
  sparkles: IconSparkles,
  checklist: IconChecklist,
  'road-map': IconRoad,
  integration: IconDeviceImacCode,
} as const;

export const NeumoAbout = ({ data }: { data: NeumoAboutProps }) => {
  const {
    badge,
    title,
    description,
    mission,
    values = [],
    image,
    imageAlt,
    ctaLabel,
    ctaHref,
    approach,
  } = data;

  return (
    <section className={classes.section} aria-labelledby="about-title">
      <div className={classes.container}>
        <div className={classes.header}>
          {badge && (
            <div className={classes.badgeWrapper}>
              <IconSparkles size={18} className={classes.badgeIcon} />
              <span className={classes.badge}>{badge}</span>
            </div>
          )}
          <h2 id="about-title" className={classes.title}>
            {title}
          </h2>
          <p className={classes.description}>{description}</p>
          {mission && <p className={classes.mission}>{mission}</p>}
        </div>

        <div className={classes.content}>
          <div className={classes.left}>
            {image && (
              <div className={classes.imageWrapper}>
                <Image
                  src={image}
                  alt={imageAlt || title}
                  fill
                  sizes="(max-width: 768px) 100vw, 50vw"
                  className={classes.image}
                />
                <div className={classes.imageOverlay} />
              </div>
            )}
          </div>

          <div className={classes.right}>
            {/* ✅ Fully dynamic, only renders if provided */}
            {approach?.title && approach.items?.length ? (
              <div className={classes.statsCard}>
                <div className={classes.statsHeader}>
                  <h3 className={classes.statsTitle}>{approach.title}</h3>
                </div>

                <ul className={classes.statsList}>
                  {approach.items.map((item, idx) => {
                    const Icon = item.icon ? iconMap[item.icon] : null;

                    return (
                      <li key={`${item.strong}-${idx}`} className={classes.statsItem}>
                        {Icon ? <Icon size={20} /> : <span className={classes.statsIconFallback} />}
                        <div>
                          <strong>{item.strong}</strong>
                          <span>{item.text}</span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}

            {ctaLabel && ctaHref && (
              <Link href={ctaHref} className={classes.cta}>
                {ctaLabel}
              </Link>
            )}
          </div>
        </div>

        {values.length > 0 && (
          <div className={classes.valuesGrid}>
            {values.map((value, index) => (
              <article key={index} className={classes.valueCard}>
                {value.icon && (
                  <div className={classes.valueIcon}>
                    <Image src={value.icon} alt="" width={32} height={32} unoptimized />
                  </div>
                )}
                <h3 className={classes.valueTitle}>{value.title}</h3>
                <p className={classes.valueDescription}>{value.description}</p>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
